import { useMemo, useState } from "react";
import UiIcon from "../components/UiIcon";
import { apiRequest, API_BASE_URL, invalidateCached } from "./api";
import { useResource } from "./hooks";
import { experienceCopy, formatDay, formatDateTime, formatNumber } from "./copy";
import { ChallengesSection, PanelError } from "./DashboardPanels";
import { FeedbackItem, StatusBadge, WsEmpty } from "./WorkspaceUi";

const MANAGED = { "data-cvi-i18n": "managed" };

function PageShell({ title, subtitle, children, onBack, language }) {
    const back = language === "fr" ? "← Retour au tableau de bord" : "← Back to dashboard";
    return <div className="cvi-dashboard-page cvi-xp-page" {...MANAGED}>
        <div className="cvi-dashboard-intro">
            <div className="cvi-dashboard-intro-copy">
                {onBack && <button type="button" className="cvi-xp-link cvi-xp-back" onClick={onBack}>{back}</button>}
                <h1 className="cvi-dashboard-title">{title}</h1>
                {subtitle && <div className="cvi-dashboard-subtitle">{subtitle}</div>}
            </div>
        </div>
        {children}
    </div>;
}

function Card({ title, aside, children, className = "" }) {
    return <section className={`cvi-manager-attention-card cvi-xp-card ${className}`}>
        {(title || aside) && <div className="cvi-manager-section-head cvi-xp-head"><div><h2>{title}</h2></div>{aside}</div>}
        {children}
    </section>;
}

function Notice({ tone = "info", children }) {
    return children ? <p className={`cvi-xp-notice is-${tone}`} role="status">{children}</p> : null;
}

const toLocalInput = date => {
    const d = new Date(date);
    const pad = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ── Check-ins ──────────────────────────────────────────────────────────────

function CheckInCard({ checkIn, language, onChanged }) {
    const t = experienceCopy(language);
    const [topic, setTopic] = useState("");
    const [action, setAction] = useState("");
    const [notes, setNotes] = useState(checkIn.followUpNotes || "");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const isManager = checkIn.viewerRole === "MANAGER";
    const other = isManager ? checkIn.employee?.name : checkIn.manager?.name;
    const run = async (path, method, body) => {
        setBusy(true); setError("");
        try {
            const result = await apiRequest(path, { method, body });
            invalidateCached("/api/workflow");
            onChanged(result.checkIn);
            return true;
        } catch (e) { setError(e.message); return false; } finally { setBusy(false); }
    };
    const base = `/api/experience/check-ins/${encodeURIComponent(checkIn.id)}`;
    const topics = checkIn.items.filter(i => i.kind === "TOPIC");
    const actions = checkIn.items.filter(i => i.kind === "ACTION");
    const editable = checkIn.status !== "CANCELLED";
    return <article className={`cvi-xp-checkin is-${checkIn.status.toLowerCase()}`}>
        <header className="cvi-xp-checkin-head">
            <div>
                <strong>{formatDateTime(checkIn.scheduledAt, language)}</strong>
                <small>{[other && t.checkIns.with(other), t.checkIns.minutes(checkIn.durationMinutes), checkIn.location].filter(Boolean).join(" · ")}</small>
            </div>
            <StatusBadge status={checkIn.status} tone={checkIn.status === "COMPLETED" ? "success" : checkIn.status === "CANCELLED" ? "neutral" : "primary"} label={t.checkIns.status[checkIn.status]} />
        </header>
        <div className="cvi-xp-checkin-cols">
            <div>
                <h3>{t.checkIns.topics}</h3>
                {topics.length ? <ul className="cvi-xp-items">{topics.map(item => <li key={item.id}>
                    <span>{item.text}<small> · {item.authorName}</small></span>
                    {item.canDelete && <button type="button" className="cvi-xp-icon-button" aria-label={t.common.remove} disabled={busy} onClick={() => run(`${base}/items/${item.id}`, "PATCH", { delete: true })}><UiIcon name="close" size={12} /></button>}
                </li>)}</ul> : <small className="cvi-xp-muted">—</small>}
                {editable && <form className="cvi-xp-inline-form" onSubmit={async e => { e.preventDefault(); if (await run(`${base}/items`, "POST", { kind: "TOPIC", text: topic })) setTopic(""); }}>
                    <label className="cvi-xp-sr" htmlFor={`topic-${checkIn.id}`}>{t.checkIns.addTopic}</label>
                    <input id={`topic-${checkIn.id}`} value={topic} maxLength={500} placeholder={t.checkIns.addTopic} onChange={e => setTopic(e.target.value)} />
                    <button type="submit" className="cvi-xp-button is-small is-secondary" disabled={busy || topic.trim().length < 2}>{t.common.add}</button>
                </form>}
            </div>
            <div>
                <h3>{t.checkIns.actions}</h3>
                {actions.length ? <ul className="cvi-xp-items">{actions.map(item => <li key={item.id}>
                    <label className="cvi-xp-check">
                        <input type="checkbox" checked={item.isDone} disabled={busy || !editable} onChange={e => run(`${base}/items/${item.id}`, "PATCH", { isDone: e.target.checked })} />
                        <span className={item.isDone ? "is-done" : ""}>{item.text}</span>
                    </label>
                </li>)}</ul> : <small className="cvi-xp-muted">—</small>}
                {isManager && editable && <form className="cvi-xp-inline-form" onSubmit={async e => { e.preventDefault(); if (await run(`${base}/items`, "POST", { kind: "ACTION", text: action })) setAction(""); }}>
                    <label className="cvi-xp-sr" htmlFor={`action-${checkIn.id}`}>{t.checkIns.addAction}</label>
                    <input id={`action-${checkIn.id}`} value={action} maxLength={500} placeholder={t.checkIns.addAction} onChange={e => setAction(e.target.value)} />
                    <button type="submit" className="cvi-xp-button is-small is-secondary" disabled={busy || action.trim().length < 2}>{t.common.add}</button>
                </form>}
            </div>
        </div>
        <div className="cvi-xp-checkin-notes">
            <h3>{t.checkIns.notes}</h3>
            {isManager && editable ? <>
                <textarea rows={3} value={notes} maxLength={4000} aria-label={t.checkIns.notes} onChange={e => setNotes(e.target.value)} />
                <div className="cvi-xp-actions">
                    <small>{t.checkIns.participantsOnly}</small>
                    <button type="button" className="cvi-xp-button is-small is-secondary" disabled={busy || notes === (checkIn.followUpNotes || "")} onClick={() => run(base, "PATCH", { followUpNotes: notes })}>{t.common.save}</button>
                </div>
            </> : <p className="cvi-xp-muted">{checkIn.followUpNotes || "—"}</p>}
        </div>
        {isManager && checkIn.status === "SCHEDULED" && <div className="cvi-xp-actions">
            <button type="button" className="cvi-xp-link" disabled={busy} onClick={() => run(base, "PATCH", { status: "CANCELLED" })}>{t.checkIns.cancelMeeting}</button>
            <button type="button" className="cvi-xp-button is-small" disabled={busy} onClick={() => run(base, "PATCH", { status: "COMPLETED", followUpNotes: notes })}>{t.checkIns.markDone}</button>
        </div>}
        {error && <p className="cvi-xp-error">{error}</p>}
    </article>;
}

export function CheckInsPage({ language, onBack }) {
    const t = experienceCopy(language);
    const { data, loading, error, reload, setData } = useResource("/api/experience/check-ins");
    const [form, setForm] = useState({ employeeId: "", scheduledAt: "", durationMinutes: "", location: "" });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");
    const replace = updated => setData(prev => {
        if (!prev) return prev;
        const swap = list => list.map(c => (c.id === updated.id ? updated : c));
        return { ...prev, asEmployee: swap(prev.asEmployee), asManager: swap(prev.asManager) };
    });
    const schedule = async e => {
        e.preventDefault();
        setSaving(true); setFormError("");
        try {
            const result = await apiRequest("/api/experience/check-ins", { method: "POST", body: { ...form, scheduledAt: new Date(form.scheduledAt).toISOString(), durationMinutes: form.durationMinutes || data.defaultDurationMinutes } });
            setData(prev => ({ ...prev, asManager: [...prev.asManager, result.checkIn].sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt)) }));
            setForm({ employeeId: "", scheduledAt: "", durationMinutes: "", location: "" });
            invalidateCached("/api/workflow");
        } catch (err) { setFormError(err.message); } finally { setSaving(false); }
    };
    const split = list => {
        const now = Date.now() - 60 * 60 * 1000;
        return {
            upcoming: list.filter(c => c.status === "SCHEDULED" && new Date(c.scheduledAt).getTime() >= now),
            past: list.filter(c => !(c.status === "SCHEDULED" && new Date(c.scheduledAt).getTime() >= now)).reverse(),
        };
    };
    if (loading && !data) return <PageShell title={t.checkIns.page} language={language} onBack={onBack}><div className="cvi-xp-empty"><small>{t.common.loading}</small></div></PageShell>;
    if (error && !data) return <PageShell title={t.checkIns.page} language={language} onBack={onBack}><PanelError language={language} onRetry={reload} /></PageShell>;
    if (!data?.enabled) return <PageShell title={t.checkIns.page} language={language} onBack={onBack}><div className="cvi-xp-empty"><small>—</small></div></PageShell>;
    const mine = split(data.asEmployee || []);
    const team = split(data.asManager || []);
    return <PageShell title={t.checkIns.page} subtitle={t.checkIns.pageSubtitle} language={language} onBack={onBack}>
        {(data.asEmployee.length > 0 || !data.reports.length) && <Card title={t.checkIns.upcoming}>
            {mine.upcoming.length ? mine.upcoming.map(c => <CheckInCard key={c.id} checkIn={c} language={language} onChanged={replace} />)
                : <div className="cvi-xp-empty"><strong>{t.checkIns.none}</strong>{data.manager && <small>{t.checkIns.noneManager}</small>}</div>}
            {mine.past.length > 0 && <details className="cvi-xp-details"><summary>{t.checkIns.past} ({mine.past.length})</summary>
                {mine.past.map(c => <CheckInCard key={c.id} checkIn={c} language={language} onChanged={replace} />)}
            </details>}
        </Card>}
        {data.reports.length > 0 && <Card title={t.checkIns.asManager}>
            <form className="cvi-xp-form-grid" onSubmit={schedule}>
                <label className="cvi-xp-field"><span>{t.checkIns.employee}</span>
                    <select required value={form.employeeId} onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))}>
                        <option value="">—</option>
                        {data.reports.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                </label>
                <label className="cvi-xp-field"><span>{t.checkIns.when}</span>
                    <input required type="datetime-local" value={form.scheduledAt} min={toLocalInput(new Date())} onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))} />
                </label>
                <label className="cvi-xp-field"><span>{t.checkIns.duration}</span>
                    <input type="number" min={10} max={240} step={5} placeholder={String(data.defaultDurationMinutes)} value={form.durationMinutes} onChange={e => setForm(f => ({ ...f, durationMinutes: e.target.value }))} />
                </label>
                <label className="cvi-xp-field"><span>{t.checkIns.location} ({t.common.optional})</span>
                    <input value={form.location} maxLength={200} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
                </label>
                <div className="cvi-xp-form-actions">
                    {formError && <span className="cvi-xp-error">{formError}</span>}
                    <button type="submit" className="cvi-xp-button" disabled={saving}>{saving ? t.common.saving : t.checkIns.schedule}</button>
                </div>
            </form>
            {team.upcoming.map(c => <CheckInCard key={c.id} checkIn={c} language={language} onChanged={replace} />)}
            {team.past.length > 0 && <details className="cvi-xp-details"><summary>{t.checkIns.past} ({team.past.length})</summary>
                {team.past.map(c => <CheckInCard key={c.id} checkIn={c} language={language} onChanged={replace} />)}
            </details>}
        </Card>}
    </PageShell>;
}

// ── Recognition ─────────────────────────────────────────────────────────────

function RecognitionList({ items, language, mode, onWithdraw }) {
    const t = experienceCopy(language).recognition;
    if (!items.length) return <WsEmpty tone="neutral" icon="star" title={t.empty} />;
    return <ul className="cvi-ws-feedback-list">
        {items.map(item => {
            const person = mode === "given" ? item.recipient?.name : item.giver?.name;
            return <li key={item.id}>
                <FeedbackItem language={language} avatarName={person || "—"}
                    author={mode === "given" ? t.to(item.recipient?.name || "—") : t.from(item.giver?.name || "—")}
                    context={mode === "organization" ? t.to(item.recipient?.name || "—") : null}
                    date={formatDay(item.createdAt, language)} category={t.categories[item.category]}
                    actions={item.canWithdraw && onWithdraw ? <button type="button" className="cvi-ws-btn is-ghost is-sm" onClick={() => onWithdraw(item)}>{t.withdraw}</button> : null}>
                    <blockquote>“{item.message}”</blockquote>
                </FeedbackItem>
            </li>;
        })}
    </ul>;
}

export function RecognitionPage({ language, users = [], currentUserId, onBack }) {
    const t = experienceCopy(language);
    const { data, loading, error, reload } = useResource("/api/experience/recognitions");
    const [form, setForm] = useState({ recipientId: "", category: "TEAM", message: "", visibility: "PRIVATE" });
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState("");
    const [formError, setFormError] = useState("");
    const colleagues = useMemo(() => users.filter(u => u.id !== currentUserId && u.isActive !== false).sort((a, b) => String(a.name).localeCompare(String(b.name))), [users, currentUserId]);
    const send = async e => {
        e.preventDefault(); setBusy(true); setFormError(""); setNotice("");
        try {
            await apiRequest("/api/experience/recognitions", { method: "POST", body: form });
            setForm(f => ({ ...f, recipientId: "", message: "" }));
            setNotice(t.recognition.sent);
            reload();
        } catch (err) { setFormError(err.message); } finally { setBusy(false); }
    };
    const withdraw = async item => {
        try { await apiRequest(`/api/experience/recognitions/${encodeURIComponent(item.id)}/withdraw`, { method: "POST" }); setNotice(t.recognition.withdrawn); reload(); }
        catch (err) { setFormError(err.message); }
    };
    if (loading && !data) return <PageShell title={t.recognition.page} language={language} onBack={onBack}><div className="cvi-xp-empty"><small>{t.common.loading}</small></div></PageShell>;
    if (error && !data) return <PageShell title={t.recognition.page} language={language} onBack={onBack}><PanelError language={language} onRetry={reload} /></PageShell>;
    if (!data?.enabled) return <PageShell title={t.recognition.page} language={language} onBack={onBack}><div className="cvi-xp-empty"><small>—</small></div></PageShell>;
    return <PageShell title={t.recognition.page} subtitle={t.recognition.pageSubtitle} language={language} onBack={onBack}>
        <Notice tone="success">{notice}</Notice>
        <div className="cvi-xp-row cvi-xp-row-2">
            {data.canGive && <Card title={t.recognition.give}>
                <form className="cvi-xp-form-stack" onSubmit={send}>
                    <label className="cvi-xp-field"><span>{t.recognition.form.recipient}</span>
                        <select required value={form.recipientId} onChange={e => setForm(f => ({ ...f, recipientId: e.target.value }))}>
                            <option value="">{t.recognition.form.pick}</option>
                            {colleagues.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                    </label>
                    <label className="cvi-xp-field"><span>{t.recognition.form.category}</span>
                        <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                            {Object.entries(t.recognition.categories).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                        </select>
                    </label>
                    <label className="cvi-xp-field"><span>{t.recognition.form.message}</span>
                        <textarea required rows={3} maxLength={500} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
                    </label>
                    <fieldset className="cvi-xp-field cvi-xp-radios"><legend>{t.recognition.form.visibility}</legend>
                        {["PRIVATE", ...(data.settings?.allowOrganizationVisibility ? ["ORGANIZATION"] : [])].map(v => <label key={v}>
                            <input type="radio" name="visibility" value={v} checked={form.visibility === v} onChange={() => setForm(f => ({ ...f, visibility: v }))} />
                            <span>{t.recognition.visibility[v]}</span>
                        </label>)}
                    </fieldset>
                    {formError && <p className="cvi-xp-error">{formError}</p>}
                    <div className="cvi-xp-actions"><small>{t.recognition.notScored}</small><button type="submit" className="cvi-xp-button" disabled={busy}>{busy ? t.common.saving : t.recognition.form.send}</button></div>
                </form>
            </Card>}
            <Card title={t.recognition.received} aside={<small className="cvi-xp-head-note">{formatNumber(data.received.length, language)}</small>}>
                <RecognitionList items={data.received} language={language} mode="received" />
            </Card>
        </div>
        <div className="cvi-xp-row cvi-xp-row-2">
            <Card title={t.recognition.given}><RecognitionList items={data.given} language={language} mode="given" onWithdraw={withdraw} /></Card>
            {data.settings?.allowOrganizationVisibility && <Card title={t.recognition.organization}><RecognitionList items={data.organization} language={language} mode="organization" onWithdraw={withdraw} /></Card>}
        </div>
    </PageShell>;
}

// ── Learning & development ─────────────────────────────────────────────────

export function LearningPage({ language, onBack }) {
    const t = experienceCopy(language);
    const { data, loading, error, reload } = useResource("/api/experience/learning");
    const [form, setForm] = useState({ title: "", provider: "", hours: "", status: "PLANNED" });
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState("");
    const save = async (path, method, body) => {
        setBusy(true); setFormError("");
        try { await apiRequest(path, { method, body }); reload(); return true; }
        catch (err) { setFormError(err.message); return false; } finally { setBusy(false); }
    };
    if (loading && !data) return <PageShell title={t.learning.page} language={language} onBack={onBack}><div className="cvi-xp-empty"><small>{t.common.loading}</small></div></PageShell>;
    if (error && !data) return <PageShell title={t.learning.page} language={language} onBack={onBack}><PanelError language={language} onRetry={reload} /></PageShell>;
    if (!data?.enabled) return <PageShell title={t.learning.page} language={language} onBack={onBack}><div className="cvi-xp-empty"><small>—</small></div></PageShell>;
    const target = data.annualTargetHours;
    const done = Number(data.completedHours) || 0;
    return <PageShell title={t.learning.page} subtitle={t.learning.pageSubtitle} language={language} onBack={onBack}>
        <div className="cvi-xp-row cvi-xp-row-2">
            <Card title={t.learning.hoursThisYear(data.year)}>
                <div className="cvi-xp-learning">
                    <strong>{target ? t.learning.ofTarget(formatNumber(done, language, { maximumFractionDigits: 1 }), formatNumber(target, language)) : `${formatNumber(done, language, { maximumFractionDigits: 1 })} h`}</strong>
                    {target ? <span className="cvi-xp-bar"><span style={{ width: `${Math.min(100, (done / target) * 100)}%` }} /></span> : <small>{t.learning.noTarget}</small>}
                    <small>{t.learning.notScored}</small>
                </div>
            </Card>
            {data.editable && <Card title={t.learning.add}>
                <form className="cvi-xp-form-grid" onSubmit={async e => { e.preventDefault(); if (await save("/api/experience/learning", "POST", form)) setForm({ title: "", provider: "", hours: "", status: "PLANNED" }); }}>
                    <label className="cvi-xp-field is-wide"><span>{t.learning.form.title}</span><input required maxLength={200} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></label>
                    <label className="cvi-xp-field"><span>{t.learning.form.provider}</span><input maxLength={200} value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))} /></label>
                    <label className="cvi-xp-field"><span>{t.learning.form.hours}</span><input type="number" min={0} max={1000} step={0.5} value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} /></label>
                    <label className="cvi-xp-field"><span>{t.learning.form.status}</span>
                        <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>{Object.entries(t.learning.status).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
                    </label>
                    <div className="cvi-xp-form-actions">{formError && <span className="cvi-xp-error">{formError}</span>}<button type="submit" className="cvi-xp-button" disabled={busy}>{t.common.add}</button></div>
                </form>
            </Card>}
        </div>
        <Card title={t.learning.recent}>
            {data.items.length === 0 ? <div className="cvi-xp-empty"><small>{t.learning.empty}</small></div> : <div className="cvi-xp-table-wrap"><table className="cvi-xp-table">
                <thead><tr><th>{t.learning.form.title}</th><th>{t.learning.form.hours}</th><th>{t.learning.form.status}</th><th><span className="cvi-xp-sr">{t.common.edit}</span></th></tr></thead>
                <tbody>{data.items.map(item => <tr key={item.id}>
                    <td><strong>{item.title}</strong>{item.provider && <small> · {item.provider}</small>}{item.completedAt && <small> · {formatDay(item.completedAt, language, { year: "numeric" })}</small>}</td>
                    <td>{formatNumber(item.hours, language, { maximumFractionDigits: 1 })}</td>
                    <td>{data.editable ? <select aria-label={t.learning.form.status} value={item.status} disabled={busy} onChange={e => save(`/api/experience/learning/${item.id}`, "PATCH", { status: e.target.value })}>
                        {Object.entries(t.learning.status).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select> : t.learning.status[item.status]}</td>
                    <td>{data.editable && <button type="button" className="cvi-xp-icon-button" aria-label={t.common.remove} disabled={busy} onClick={() => save(`/api/experience/learning/${item.id}`, "PATCH", { delete: true })}><UiIcon name="close" size={12} /></button>}</td>
                </tr>)}</tbody>
            </table></div>}
        </Card>
    </PageShell>;
}

// ── Team challenges (managers) ─────────────────────────────────────────────

export function TeamChallengesPage({ language, onBack }) {
    const t = experienceCopy(language).challenges;
    return <PageShell title={t.team} subtitle={t.teamSubtitle} language={language} onBack={onBack}>
        <ChallengesSection language={language} scope="team" title={t.team} />
    </PageShell>;
}

// ── Privacy & data rights ──────────────────────────────────────────────────

export function PrivacyPage({ language, onBack }) {
    const t = experienceCopy(language);
    const p = t.privacy;
    const { data, loading, error, reload } = useResource("/api/experience/privacy");
    const [form, setForm] = useState({ requestType: "ACCESS", details: "" });
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState("");
    const [formError, setFormError] = useState("");
    const fr = language === "fr";
    const send = async e => {
        e.preventDefault(); setBusy(true); setFormError(""); setNotice("");
        try { await apiRequest("/api/experience/privacy/requests", { method: "POST", body: form }); setForm({ requestType: "ACCESS", details: "" }); setNotice(p.sent); reload(); }
        catch (err) { setFormError(err.message); } finally { setBusy(false); }
    };
    const download = async () => {
        setFormError(""); setNotice("");
        try {
            let token = null;
            try { token = sessionStorage.getItem("cvi_token"); } catch { token = null; }
            const response = await fetch(`${API_BASE_URL}/api/experience/privacy/export`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!response.ok) throw new Error((await response.json().catch(() => ({}))).message || "Export failed");
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `cvi-my-data-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
            setNotice(p.exportDone);
        } catch (err) { setFormError(err.message); }
    };
    if (loading && !data) return <PageShell title={p.page} language={language} onBack={onBack}><div className="cvi-xp-empty"><small>{t.common.loading}</small></div></PageShell>;
    if (error && !data) return <PageShell title={p.page} language={language} onBack={onBack}><PanelError language={language} onRetry={reload} /></PageShell>;
    const pv = data.privacy;
    const notice_ = fr ? (pv.noticeFr || pv.noticeEn) : (pv.noticeEn || pv.noticeFr);
    const retention = fr ? (pv.retentionFr || pv.retentionEn) : (pv.retentionEn || pv.retentionFr);
    return <PageShell title={p.page} subtitle={p.subtitle} language={language} onBack={onBack}>
        <Notice tone="success">{notice}</Notice>
        <div className="cvi-xp-row cvi-xp-row-2">
            <Card title={p.notice}><p className="cvi-xp-prose">{notice_ || p.noNotice}</p></Card>
            <Card title={p.access}>
                <dl className="cvi-xp-access">
                    {p.accessRows.map(([who, what]) => <div key={who}><dt>{who}</dt><dd>{what}</dd></div>)}
                </dl>
            </Card>
        </div>
        <div className="cvi-xp-row cvi-xp-row-2">
            <Card title={p.retention}><p className="cvi-xp-prose">{retention || p.noRetention}</p></Card>
            <Card title={p.officer}>
                {pv.officerName || pv.officerEmail ? <p className="cvi-xp-prose">{pv.officerName}{pv.officerEmail && <><br /><a href={`mailto:${pv.officerEmail}`}>{pv.officerEmail}</a></>}</p> : <p className="cvi-xp-prose">{p.noOfficer}</p>}
            </Card>
        </div>
        <Card title={p.requests} aside={pv.exportEnabled ? <button type="button" className="cvi-xp-button is-small is-secondary" onClick={download}><UiIcon name="archive" size={13} />{p.export}</button> : null}>
            {pv.exportEnabled && <small className="cvi-xp-muted">{p.exportHelp}</small>}
            {data.requests.length ? <ul className="cvi-xp-requests">{data.requests.map(r => <li key={r.id}>
                <div><strong>{p.types[r.requestType] || r.requestType}</strong><small>{formatDay(r.createdAt, language, { year: "numeric" })}{r.details ? ` · ${r.details}` : ""}</small>{r.response && <small className="cvi-xp-response">{r.response}</small>}</div>
                <StatusBadge status={r.status} tone={r.status === "COMPLETED" ? "success" : r.status === "DECLINED" ? "neutral" : "info"} label={p.status[r.status]} />
            </li>)}</ul> : <div className="cvi-xp-empty"><small>{p.none}</small></div>}
            {pv.requestsEnabled ? <form className="cvi-xp-form-grid" onSubmit={send}>
                <label className="cvi-xp-field"><span>{p.type}</span>
                    <select value={form.requestType} onChange={e => setForm(f => ({ ...f, requestType: e.target.value }))}>{Object.entries(p.types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
                </label>
                <label className="cvi-xp-field is-wide"><span>{p.details}{form.requestType === "ACCESS" ? ` (${t.common.optional})` : ""}</span>
                    <textarea rows={2} maxLength={2000} required={form.requestType === "CORRECTION"} value={form.details} onChange={e => setForm(f => ({ ...f, details: e.target.value }))} />
                </label>
                <div className="cvi-xp-form-actions">{formError && <span className="cvi-xp-error">{formError}</span>}<button type="submit" className="cvi-xp-button" disabled={busy}>{busy ? t.common.saving : p.submit}</button></div>
            </form> : <small className="cvi-xp-muted">{p.requestsOff}</small>}
        </Card>
    </PageShell>;
}
